// Minimal toast queue — the standard shadcn pattern with a global reducer
// store. Components call toast() to enqueue; <Toaster /> renders them.
import * as React from 'react'
import type { ToastProps } from './toast'

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
}

const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 4000

type State = { toasts: ToasterToast[] }
const listeners: Array<(state: State) => void> = []
let memoryState: State = { toasts: [] }

function emit(next: State) {
  memoryState = next
  listeners.forEach((l) => l(memoryState))
}

const timeouts = new Map<string, ReturnType<typeof setTimeout>>()
function scheduleRemoval(id: string) {
  if (timeouts.has(id)) return
  const t = setTimeout(() => {
    timeouts.delete(id)
    emit({ toasts: memoryState.toasts.filter((x) => x.id !== id) })
  }, TOAST_REMOVE_DELAY)
  timeouts.set(id, t)
}

let counter = 0
export function toast(props: Omit<ToasterToast, 'id'>) {
  const id = String(++counter)
  const next: ToasterToast = { ...props, id, open: true, onOpenChange: (open) => !open && dismiss(id) }
  emit({ toasts: [next, ...memoryState.toasts].slice(0, TOAST_LIMIT) })
  scheduleRemoval(id)
  return { id, dismiss: () => dismiss(id) }
}

export function dismiss(id?: string) {
  if (id === undefined) {
    emit({ toasts: memoryState.toasts.map((x) => ({ ...x, open: false })) })
  } else {
    emit({ toasts: memoryState.toasts.map((x) => (x.id === id ? { ...x, open: false } : x)) })
  }
}

export function useToast() {
  const [state, setState] = React.useState<State>(memoryState)
  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const i = listeners.indexOf(setState)
      if (i > -1) listeners.splice(i, 1)
    }
  }, [])
  return { toasts: state.toasts, toast, dismiss }
}
